import { useRouter } from '@tanstack/react-router'
import { ArrowRightIcon, LogOutIcon } from 'lucide-react'

import { Button, Card, Link } from '@/components'
import {
  TRIAL_LENGTH_DAYS,
  TRIAL_PAGE_GRANT,
  useAccountMutating,
} from '@/modules/account'
import { AlreadyMemberNotice } from '../components/AlreadyMemberNotice'
import { TrialPageFrame } from '../components/TrialPageFrame'
import { useStartTrial } from '../hooks/useStartTrial'
import { useAuthMutations, useUser } from '../stores'
import type { TrialSignUpDetails } from '../utils/onboarding'

interface TrialStartPageProps {
  details: TrialSignUpDetails
}

export const TrialStartPage = ({ details }: TrialStartPageProps) => {
  const router = useRouter()
  const { signOut } = useAuthMutations()
  const email = useUser()?.email
  const logOut = () => signOut().then(() => router.navigate({ to: '/login' }))

  return (
    <TrialPageFrame
      headerAction={
        <Button theme="ghost" onClick={logOut}>
          <LogOutIcon />
          Log out
        </Button>
      }
    >
      <main className="flex flex-1 items-center justify-center">
        <TrialStartCard details={details} />
      </main>
      <p className="text-center text-[13px] text-muted-foreground">
        Signed in as {email}. Not you?{' '}
        <Link asChild theme="brand" className="font-medium">
          <button type="button" onClick={logOut}>
            Log out
          </button>
        </Link>
      </p>
    </TrialPageFrame>
  )
}

const TrialStartCard = ({ details }: TrialStartPageProps) => {
  const { startTrial, isAlreadyMember } = useStartTrial()
  const isStartingTrial = useAccountMutating()

  return (
    <Card className="flex w-full max-w-120 flex-col gap-6 p-6 sm:p-10">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-balance">
          Start your free trial.
        </h1>
        <p className="text-[15px] text-muted-foreground">
          Your trial gives you {TRIAL_LENGTH_DAYS} days and {TRIAL_PAGE_GRANT}{' '}
          pages, with full reconciliation.
        </p>
      </div>
      <div className="flex flex-col gap-2.5">
        <Button
          size="lg"
          className="w-full"
          isLoading={isStartingTrial}
          loadingText="Starting trial..."
          onClick={() => startTrial(details)}
        >
          Start free trial
          <ArrowRightIcon />
        </Button>
        {isAlreadyMember && <AlreadyMemberNotice />}
      </div>
    </Card>
  )
}
